import { sceneHasQuestions } from "./scene_mandatory";

// How the global "Scene defaults" card choice lands on each scene.
//
// Two flags govern the scene-end card and which one is live depends on
// auto-advance: `autoNextShowCta` while the scene auto-advances, `sceneEndCta`
// while it does not. A question scene never auto-advances (the Visor blocks it
// so the learner can read the answer feedback), so the second one owns its
// card — and a course-wide "auto-advance + show a countdown" used to write only
// the first, silently skipping exactly those scenes: the author ended up with
// no card and, in the editor, no toggle either.
//
// Applied by both paths that push the defaults onto every scene
// (`toBatchMandatoryPayload` and `applyMandatoryToAllScenes`).

/**
 * @param {object} mandatory the global defaults ({ autoNext, autoNextShowCta,
 *   sceneEndCta, ... })
 * @returns {{autoNextShowCta: boolean, sceneEndCta: boolean}}
 */
export function resolveSceneCardFlags({ autoNext, autoNextShowCta, sceneEndCta } = {}) {
  // "Show a countdown before advancing" and "show a transition card" are the
  // same intent seen from two scene types, so with auto-advance on they carry
  // the same value. With it off there is no countdown to speak of.
  return {
    autoNextShowCta: autoNext ? !!autoNextShowCta : false,
    sceneEndCta: autoNext ? !!autoNextShowCta : !!sceneEndCta,
  };
}

/**
 * The transition fields a scene should be BORN with, taken from the global
 * "Scene defaults" section. Without this the defaults only ever reached the
 * scenes that happened to exist when the author pressed Save — anything added
 * afterwards came out with the hardcoded creator defaults instead.
 *
 * Deliberately narrow: auto-advance and its card, nothing else. The mandatory
 * fields are NOT carried over, because the two sides disagree on their default
 * (`toolbarElementCreator` stamps `isMandatory: true`, the global section
 * defaults to `false`) and copying them across would silently flip the Next-
 * button gate on every scene added in every course that never opened the
 * global config.
 *
 * @param {object} globalConfig the undoGroup `globalConfig` slice
 * @returns {?object} fields to merge onto a new viewToolbar, or null when the
 *   course carries no defaults yet
 */
export function sceneDefaultsFromGlobalConfig(globalConfig) {
  const mandatory = globalConfig && globalConfig.defaultSceneMandatory;
  if (!mandatory || typeof mandatory !== "object") return null;
  return {
    autoNext: !!mandatory.autoNext,
    ...resolveSceneCardFlags(mandatory),
  };
}

// Answer-gated rules; a scene without questions can never satisfy them.
export const QUESTION_MANDATORY_TYPES = ["waitForAction", "waitForCorrectAction"];

/**
 * Splits the global defaults into standard-scene and question-scene rules.
 * A legacy single `mandatoryType` carries over to the question side.
 */
export function normalizeSceneMandatoryDefaults(mandatory) {
  const m = mandatory && typeof mandatory === "object" ? mandatory : {};
  const allowed = ["waitForTime", ...QUESTION_MANDATORY_TYPES];
  const pick = (type) => (allowed.includes(type) ? type : null);
  const positive = (n) => (typeof n === "number" && n > 0 ? n : 0);
  // 0 = empty: the scene waits for its own duration.
  const minTime = positive(m.minTime);
  return {
    isMandatory: false,
    autoNext: false,
    autoNextShowCta: false,
    sceneEndCta: false,
    ...m,
    minTime,
    mandatoryType: "waitForTime",
    questionIsMandatory:
      typeof m.questionIsMandatory === "boolean" ? m.questionIsMandatory : !!m.isMandatory,
    questionMinTime: typeof m.questionMinTime === "number" ? m.questionMinTime : minTime,
    questionMandatoryType:
      pick(m.questionMandatoryType) || pick(m.mandatoryType) || "waitForAction",
  };
}

/** viewToolbar payloads for scenes without (`scene`) and with (`question`) questions. */
export function sceneMandatoryPayloads(mandatory) {
  const m = normalizeSceneMandatoryDefaults(mandatory);
  const transition = { autoNext: !!m.autoNext, ...resolveSceneCardFlags(m) };
  return {
    scene: {
      isMandatory: !!m.isMandatory,
      mandatoryType: "waitForTime",
      ...transition,
      slideDuration:
        m.isMandatory && typeof m.minTime === "number" && m.minTime > 0 ? m.minTime : null,
    },
    question: {
      isMandatory: m.questionIsMandatory,
      mandatoryType: m.questionIsMandatory ? m.questionMandatoryType : "waitForTime",
      ...transition,
      // null = gate on the scene's own duration.
      ...(m.questionIsMandatory && m.questionMandatoryType === "waitForTime"
        ? { slideDuration: m.questionMinTime > 0 ? m.questionMinTime : null }
        : {}),
    },
  };
}

export function questionSceneIds(present) {
  return Object.keys((present && present.viewToolbarsById) || {}).filter((id) =>
    sceneHasQuestions(present, id)
  );
}

/** Falls back to the time rule on scenes saved with an answer rule but no question. */
export function repairQuestionMandatoryOnPlainScenes(present) {
  const toolbars = present && present.viewToolbarsById;
  if (!toolbars) return toolbars;
  let repaired = toolbars;
  Object.keys(toolbars).forEach((id) => {
    const tb = toolbars[id];
    if (!tb || !tb.isMandatory || !QUESTION_MANDATORY_TYPES.includes(tb.mandatoryType)) return;
    if (sceneHasQuestions(present, id)) return;
    if (repaired === toolbars) repaired = { ...toolbars };
    repaired[id] = { ...tb, mandatoryType: "waitForTime" };
  });
  return repaired;
}
